import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { QueuedOp, QueuedOpKind, StoreBridge } from './queue.types';

const STORAGE_KEY = 'pos_sync_queue';

@Injectable({ providedIn: 'root' })
export class LocalQueueService {
  private readonly platformId = inject(PLATFORM_ID);

  private get bridge(): StoreBridge | null {
    const w = globalThis as unknown as { posBridge?: { store?: StoreBridge } };
    return w.posBridge?.store ?? null;
  }

  async enqueue(kind: QueuedOpKind, payload: unknown, clientUuid: string): Promise<QueuedOp> {
    const ops = await this.readAll();
    const existing = ops.find((o) => o.clientUuid === clientUuid && o.kind === kind);
    if (existing) {
      return existing;
    }
    const op: QueuedOp = {
      id: makeId(),
      kind,
      clientUuid,
      payload,
      status: 'pending',
      attempts: 0,
      createdAt: Date.now(),
    };
    ops.push(op);
    await this.writeAll(ops);
    return op;
  }

  async pending(): Promise<QueuedOp[]> {
    return this.readAll();
  }

  async pendingCount(): Promise<number> {
    return (await this.readAll()).length;
  }

  async markSent(id: string): Promise<void> {
    const ops = (await this.readAll()).filter((o) => o.id !== id);
    await this.writeAll(ops);
  }

  async markFailed(id: string, error: string): Promise<void> {
    const ops = await this.readAll();
    const op = ops.find((o) => o.id === id);
    if (op) {
      op.status = 'failed';
      op.attempts += 1;
      op.lastError = error;
      await this.writeAll(ops);
    }
  }

  async clear(): Promise<void> {
    await this.writeAll([]);
  }

  private async readAll(): Promise<QueuedOp[]> {
    if (this.bridge) {
      return this.bridge.load();
    }
    if (!isPlatformBrowser(this.platformId)) {
      return [];
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as QueuedOp[]) : [];
    } catch {
      return [];
    }
  }

  private async writeAll(ops: QueuedOp[]): Promise<void> {
    if (this.bridge) {
      await this.bridge.save(ops);
      return;
    }
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ops));
    }
  }
}

function makeId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c?.randomUUID ? c.randomUUID() : `op-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
